import classNames from "classnames";
import { html } from "lit-html";
import i18next from "i18next";
import { useRef } from "Shared/haunted/CustomHooks";
import { useEffectWithoutCleanup, useMemo, useState } from "Shared/haunted/CustomHooks";
import { useClickOutside } from "Shared/haunted/useClickOutside";
import { ref } from "Components/directives/ref";
import {
	CHILE_COUNTRY_CODE,
	COLOMBIA_COUNTRY_CODE,
	COLOMBIAN_CULTURE_CODE,
	MIN_NUMBER_FOR_GROUP_BOOKINGS,
} from "Services/constants";
import { CLASS_NAMES } from "Services/ClassNames";
import { isTabletOrMobileResolution } from "Services/common";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { passengersUtils, PaxType, WarningKey } from "./passengersUtils";
import { maxChildrenAmount, maxInfantAmount, passengerRowTemplate } from "ComponentHelpers/PassengerSelectorHelper";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import {
	DisabledPassengerCountStatesDto,
	PassengerSelectorActionsDto,
	WarningsDto,
	useMobileSearchboxPassengerSelectorModal,
} from "./useMobileSearchboxPassengerSelectorModal";

const { createWarningsMap } = passengersUtils();

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
}

export const usePassengerSelector = (props: Props) => {
	const root = useRef<HTMLDivElement>(null);
	const dropdownElem = useRef<HTMLDivElement>(null);

	const { adults, teens, children, infants } = props.currentSearchManager.currentSearch.passengers;

	const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
	const [isWithGroupBookingWarning, setIsWithGroupBookingWarning] = useState(false);
	const [isWithTeenInfantRemoveWarning, setIsWithTeenInfantRemoveWarning] = useState(false);
	const [warningKeys, setWarningKeys] = useState<WarningKey[]>([]);

	const total = adults + teens + children + infants;

	const isColombianDomesticFlight = useMemo(
		() =>
			props.currentSearchManager.currentSearch.originCity?.countryCode === COLOMBIA_COUNTRY_CODE &&
			props.currentSearchManager.currentSearch.destinationCity?.countryCode === COLOMBIA_COUNTRY_CODE,
		[
			props.currentSearchManager.currentSearch.originCity?.countryCode,
			props.currentSearchManager.currentSearch.destinationCity?.countryCode,
		]
	);

	const isChileanSearch = () =>
		props.currentSearchManager.currentSearch.originCity?.countryCode === CHILE_COUNTRY_CODE ||
		props.currentSearchManager.currentSearch.destinationCity?.countryCode === CHILE_COUNTRY_CODE;

	const isAtGroupLimit = () => adults + teens + children === MIN_NUMBER_FOR_GROUP_BOOKINGS - 1;
	const isAddAdultsDisabled = () => isAtGroupLimit();
	const isAddTeensDisabled = () => isAtGroupLimit();
	const isAddChildrenDisabled = () =>
		children >= maxChildrenAmount(props.currentSearchManager.currentSearch.passengers) || isAtGroupLimit();
	const isRemoveAdultsDisabled = () => (adults === 1 && teens === 0) || adults === 0;
	const isRemoveTeensDisabled = () => (adults === 0 && teens === 1) || teens === 0;
	const isRemoveChildrenDisabled = () => children === 0;
	const isAddInfantsDisabled = () => infants >= maxInfantAmount(props.currentSearchManager.currentSearch.passengers);
	const isRemoveInfantsDisabled = () => infants === 0;

	const warningsMap = createWarningsMap(props.culture, isChileanSearch());

	const initDesktopDropdown = () => {
		const onScroll = () => setIsDesktopDropdownOpen(false);
		document.addEventListener("scroll", onScroll);
		useClickOutside([dropdownElem, root], () => setIsDesktopDropdownOpen(false));
	};

	const checkForGroupBookingWarning = (type: PaxType) => {
		if (type !== "infants" && isAtGroupLimit()) {
			setIsWithGroupBookingWarning(true);
			return;
		}

		setIsWithGroupBookingWarning(false);
	};

	const checkForTeenInfantRemoveWarning = (type: PaxType) => {
		if (type !== "adults" && type !== "teens") {
			setIsWithTeenInfantRemoveWarning(false);
			return;
		}

		const adultsNew = type === "adults" ? adults - 1 : adults;
		const teensNew = type === "teens" ? teens - 1 : teens;

		const newPassengers = {
			...props.currentSearchManager.currentSearch.passengers,
			adults: adultsNew,
			teens: teensNew,
		};

		if (infants > maxInfantAmount(newPassengers)) {
			setIsWithTeenInfantRemoveWarning(true);
			return;
		}

		setIsWithTeenInfantRemoveWarning(false);
	};

	const handleIncreasePaxNumber = (type: PaxType, isDisabled: boolean) => {
		if (isDisabled) {
			checkForGroupBookingWarning(type);
			return;
		}

		setIsWithGroupBookingWarning(false);
		setIsWithTeenInfantRemoveWarning(false);

		props.currentSearchManager.setPassengers({
			...props.currentSearchManager.currentSearch.passengers,
			[type]: props.currentSearchManager.currentSearch.passengers[type] + 1,
		});
	};

	const handleDecreasePaxNumber = (type: PaxType, isDisabled: boolean) => {
		setIsWithGroupBookingWarning(false);
		checkForTeenInfantRemoveWarning(type);

		if (isDisabled) return;

		props.currentSearchManager.setPassengers({
			...props.currentSearchManager.currentSearch.passengers,
			[type]: props.currentSearchManager.currentSearch.passengers[type] - 1,
		});
	};

	// Mobile modal
	const disabledStatesDto: DisabledPassengerCountStatesDto = {
		isAddAdultsDisabled: isAddAdultsDisabled(),
		isAddTeensDisabled: isAddTeensDisabled(),
		isAddChildrenDisabled: isAddChildrenDisabled(),
		isAddInfantsDisabled: isAddInfantsDisabled(),
		isRemoveAdultsDisabled: isRemoveAdultsDisabled(),
		isRemoveTeensDisabled: isRemoveTeensDisabled(),
		isRemoveChildrenDisabled: isRemoveChildrenDisabled(),
		isRemoveInfantsDisabled: isRemoveInfantsDisabled(),
	};

	const actionsDto: PassengerSelectorActionsDto = {
		onIncreasePaxNumber: handleIncreasePaxNumber,
		onDecreasePaxNumber: handleDecreasePaxNumber,
		onConfirm: (e: MouseEvent) => {
			mobilePassengerSelectorModal.close(e);
		},
	};

	const warningsDto: WarningsDto = {
		isWithGroupBookingWarning,
		isWithTeenInfantRemoveWarning,
		isColombianDomesticFlight,
		isChileanSearch: isChileanSearch(),
	};

	const mobilePassengerSelectorModal = useMobileSearchboxPassengerSelectorModal({
		culture: props.culture,
		passengers: props.currentSearchManager.currentSearch.passengers,
		warnings: warningsDto,
		disabledStates: disabledStatesDto,
		actions: actionsDto,
	});

	const handleMobileInputClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		PUB_SUBS.MobileSearchboxModalOpened.publish();
		mobilePassengerSelectorModal.open();
	};

	useEffectWithoutCleanup(() => {
		const newPassengers = { ...props.currentSearchManager.currentSearch.passengers };

		if (newPassengers.infants > maxInfantAmount(newPassengers)) {
			newPassengers.infants = maxInfantAmount(newPassengers);
		}
		if (newPassengers.children > maxChildrenAmount(newPassengers)) {
			newPassengers.children = maxChildrenAmount(newPassengers);
		}

		if (
			newPassengers.infants !== props.currentSearchManager.currentSearch.passengers.infants ||
			newPassengers.children !== props.currentSearchManager.currentSearch.passengers.children
		) {
			props.currentSearchManager.setPassengers(newPassengers);
		}
	}, [adults, teens, children, infants]);

	useEffectWithoutCleanup(() => {
		const newWarnings: WarningKey[] = [];

		if (isWithGroupBookingWarning) newWarnings.push("groupBooking");

		if (isWithTeenInfantRemoveWarning) newWarnings.push("teenInfantRemove");

		if (children > 0 || infants > 0) newWarnings.push("minorOrInfantAge");

		if (children > 0 && isColombianDomesticFlight && props.culture.toLowerCase() === COLOMBIAN_CULTURE_CODE) {
			newWarnings.push("childrenAgeWarningColombia");
		}

		if (teens > 0) newWarnings.push("teenLegalGuardian");

		setWarningKeys(newWarnings);
	}, [props.currentSearchManager.currentSearch.passengers, isWithGroupBookingWarning, isWithTeenInfantRemoveWarning]);

	useEffectWithoutCleanup(() => {
		setIsWithGroupBookingWarning(false);
		setIsWithTeenInfantRemoveWarning(false);
	}, [isDesktopDropdownOpen, mobilePassengerSelectorModal.isOpen]);

	useEffectWithoutCleanup(initDesktopDropdown, [isDesktopDropdownOpen]);

	const handleAdultsIncrease = () => handleIncreasePaxNumber("adults", isAddAdultsDisabled());
	const handleAdultsDecrease = () => handleDecreasePaxNumber("adults", isRemoveAdultsDisabled());
	const handleTeensIncrease = () => handleIncreasePaxNumber("teens", isAddTeensDisabled());
	const handleTeensDecrease = () => handleDecreasePaxNumber("teens", isRemoveTeensDisabled());
	const handleChildrenIncrease = () => handleIncreasePaxNumber("children", isAddChildrenDisabled());
	const handleChildrenDecrease = () => handleDecreasePaxNumber("children", isRemoveChildrenDisabled());
	const handleInfantsIncrease = () => handleIncreasePaxNumber("infants", isAddInfantsDisabled());
	const handleInfantsDecrease = () => handleDecreasePaxNumber("infants", isRemoveInfantsDisabled());
	const handleConfirm = () => setIsDesktopDropdownOpen(false);
	const handleInputClick = () => setIsDesktopDropdownOpen(!isDesktopDropdownOpen);

	const adultsTemplate = () =>
		passengerRowTemplate({
			amount: adults,
			info: i18next.t("18 o más años"),
			isAddDisabled: isAddAdultsDisabled(),
			isRemoveDisabled: isRemoveAdultsDisabled(),
			label: i18next.t("Adultos"),
			onDecrease: handleAdultsDecrease,
			onIncrease: handleAdultsIncrease,
		});

	const teensTemplate = () =>
		passengerRowTemplate({
			amount: teens,
			info: isChileanSearch() ? i18next.t("14-17 años") : i18next.t("13-17 años"),
			isAddDisabled: isAddTeensDisabled(),
			isRemoveDisabled: isRemoveTeensDisabled(),
			label: i18next.t("Adolescentes"),
			onDecrease: handleTeensDecrease,
			onIncrease: handleTeensIncrease,
		});

	const childrenTemplate = () =>
		passengerRowTemplate({
			amount: children,
			info: isChileanSearch() ? i18next.t("2-13 años") : i18next.t("2-12 años"),
			isAddDisabled: isAddChildrenDisabled(),
			isRemoveDisabled: isRemoveChildrenDisabled(),
			label: i18next.t("Niños"),
			onDecrease: handleChildrenDecrease,
			onIncrease: handleChildrenIncrease,
		});

	const infantsTemplate = () =>
		passengerRowTemplate({
			amount: infants,
			info: i18next.t("0-23 meses"),
			isAddDisabled: isAddInfantsDisabled(),
			isRemoveDisabled: isRemoveInfantsDisabled(),
			label: i18next.t("Infantes"),
			onDecrease: handleInfantsDecrease,
			onIncrease: handleInfantsIncrease,
		});

	const submitTemplate = () => html`
		<div
			class="flex w-full cursor-pointer items-center justify-center rounded-full bg-n-cyan py-3 font-body text-sm font-semibold text-white hover:bg-n-blue"
			@click=${handleConfirm}
		>
			<div class="flex items-center justify-center">
				${i18next.t("Confirmar")} <i class="jsh-circle-chevron-right1 ml-2 mt-px text-sm"></i>
			</div>
		</div>
	`;

	const warningsTemplate = () => {
		if (warningKeys.length === 0) return "";
		const warningItemsTemplate = warningKeys.map(
			(key) => html`<div class="mt-2 font-body text-xs text-n-blue">${warningsMap.get(key)}</div>`
		);

		return html`<div class="mt-2 flex flex-col items-start">${warningItemsTemplate}</div>`;
	};

	const dropdownTemplate = () => html`
		<div
			ref=${ref(dropdownElem)}
			class="max-h-full w-68 max-w-full flex-row items-stretch overflow-hidden overflow-y-scroll rounded-lg bg-white p-4"
		>
			${adultsTemplate()} ${teensTemplate()} ${childrenTemplate()} ${infantsTemplate()} ${submitTemplate()}
			${warningsTemplate()}
		</div>
	`;

	const inputTemplate = () => html`
		<div
			class=${classNames(
				"flex w-full cursor-pointer items-center justify-between rounded-lg border border-solid bg-white px-4 h-[42px]",
				CLASS_NAMES.flightChangeSearchPassengerSelectorClass,
				{
					"border-n-cyan": isDesktopDropdownOpen,
					"border-n-blue": !isDesktopDropdownOpen,
				}
			)}
			@click=${handleInputClick}
		>
			<div class="font-body text-base text-n-blue">
				${total} ${total === 1 ? i18next.t("pasajero") : i18next.t("pasajeros")}
			</div>
			<i class="jsh-user text-base text-n-gray"></i>
		</div>
	`;

	const mobileInputTemplate = () => html`
		<div
			class=${classNames(
				"flex h-[34px] w-full cursor-pointer items-center justify-between rounded-lg border border-solid bg-white px-2",
				"border-n-blue"
			)}
			@click=${handleMobileInputClick}
		>
			<div class="font-body text-base text-n-blue">
				${total} ${total === 1 ? i18next.t("pasajero") : i18next.t("pasajeros")}
			</div>
			<i class="jsh-user text-base text-n-gray"></i>
		</div>
		${mobilePassengerSelectorModal.htmlTemplate()}
	`;

	const desktopDropdownTemplate = () => html`
		<div ref=${ref(root)} class="relative">
			${inputTemplate()}
			<ac-dropdown2 .content=${dropdownTemplate()} .isOpen=${isDesktopDropdownOpen}></ac-dropdown2>
		</div>
	`;

	const htmlTemplate = () => (isTabletOrMobileResolution() ? mobileInputTemplate() : desktopDropdownTemplate());

	return {
		htmlTemplate,
		open: () => {
			if (isTabletOrMobileResolution()) {
				PUB_SUBS.MobileSearchboxModalOpened.publish();
				mobilePassengerSelectorModal.open();
			} else {
				setIsDesktopDropdownOpen(true);
			}
		},
	};
};
